import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { User, Mail, Building, Phone, Briefcase, Server, MessageSquare, Send } from 'lucide-react';
import { motion } from "framer-motion";

const contactFormSchema = (t) => z.object({
  name: z.string().min(1, { message: t('homepage.contact.errors.name', "Name is required") }),
  email: z.string()
    .min(1, { message: t('homepage.contact.errors.email', "Email is required") })
    .email({ message: t('homepage.contact.errors.emailInvalid', "Email is invalid") }),
  phone: z.string().optional(),
  company: z.string().optional(),
  serviceType: z.string().min(1, { message: t('homepage.contact.errors.serviceType', "Please select a service type") }),
  devices: z.string().min(1, { message: t('homepage.contact.errors.devices', "Please select number of devices") }),
  message: z.string().min(10, { message: t('homepage.contact.errors.message', "Message must be at least 10 characters") }),
});

export default function ContactForm() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const form = useForm({
    resolver: zodResolver(contactFormSchema(t)),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      serviceType: "",
      devices: "",
      message: "",
    },
  });

  const serviceTypeOptions = [
    { value: "fleet", labelKey: "homepage.contact.serviceTypes.fleet" },
    { value: "edge", labelKey: "homepage.contact.serviceTypes.edge" },
    { value: "gateway", labelKey: "homepage.contact.serviceTypes.gateway" },
    { value: "other", labelKey: "homepage.contact.serviceTypes.other" },
  ];

  const deviceOptions = [
    { value: "notSure", labelKey: "homepage.contact.deviceOptions.notSure" },
    { value: "small", labelKey: "homepage.contact.deviceOptions.small" },
    { value: "medium", labelKey: "homepage.contact.deviceOptions.medium" },
    { value: "large", labelKey: "homepage.contact.deviceOptions.large" },
    { value: "enterprise", labelKey: "homepage.contact.deviceOptions.enterprise" },
  ];

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    console.log("Contact form submitted with:", values);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const success = true;
      if (success) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
        form.setError("root.serverError", { 
          type: "manual", 
          message: t('homepage.contact.errors.submit', "Failed to send message. Please try again.") 
        });
      }
    } catch (error) {
      setSubmitStatus('error');
      form.setError("root.serverError", { type: "manual", message: t('homepage.contact.errors.submit') });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (submitStatus === 'success') {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-green-50 dark:bg-green-900/30 rounded-lg shadow-lg">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}>
          <Mail size={64} className="text-green-500 dark:text-green-400 mb-4" />
        </motion.div>
        <h3 className="text-2xl font-semibold text-green-700 dark:text-green-300 mb-2">
          {t('homepage.contact.success.title', "Message Sent!")}
        </h3>
        <p className="text-muted-foreground mb-6">
          {t('homepage.contact.success.message', "Thank you for reaching out. We will get back to you as soon as possible.")}
        </p>
        <Button onClick={() => setSubmitStatus(null)} variant="outline">
          {t('homepage.contact.success.back', "Send Another Message")}
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center text-muted-foreground">
                  <User size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                  {t("homepage.contact.fields.name", "Full Name")}
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t("homepage.contact.fields.name", "Full Name")} 
                    {...field} 
                    className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center text-muted-foreground">
                  <Mail size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                  {t("homepage.contact.fields.email", "Email Address")}
                </FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder={t("loginpage.placeholders.email", "you@example.com")}
                    {...field} 
                    className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center text-muted-foreground">
                  <Phone size={14} className={cn("text-muted-foreground/80 mr-1.5")} />
                  {t("homepage.contact.fields.phone", "Phone Number (Optional)")}
                </FormLabel>
                <FormControl>
                  <Input 
                    type="tel" 
                    placeholder={t("homepage.contact.fields.phone", "Phone Number (Optional)")} 
                    {...field} 
                    className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center text-muted-foreground">
                  <Building size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                  {t("homepage.contact.fields.company", "Company Name (Optional)")}
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t("homepage.contact.fields.company", "Company Name (Optional)")} 
                    {...field} 
                    className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="serviceType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center text-muted-foreground">
                <Briefcase size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                {t("homepage.contact.fields.serviceType", "Service of Interest")}
              </FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                dir={isRTL ? "rtl" : "ltr"}
              >
                <FormControl>
                  <SelectTrigger className="bg-background data-[placeholder]:text-muted-foreground/50">
                    <SelectValue placeholder={t("homepage.contact.placeholders.serviceType", "Select PaaS service area...")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-background border shadow-md">
                  {serviceTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey, option.value.charAt(0).toUpperCase() + option.value.slice(1))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="devices"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center text-muted-foreground">
                <Server size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                {t("homepage.contact.fields.devices", "Number of Assets")}
              </FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                dir={isRTL ? "rtl" : "ltr"}
              >
                <FormControl>
                  <SelectTrigger className="bg-background data-[placeholder]:text-muted-foreground/50">
                    <SelectValue placeholder={t("homepage.contact.placeholders.devices", "Est. number of assets for PaaS...")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-background border shadow-md">
                  {deviceOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey, option.labelKey.split('.').pop())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center text-muted-foreground">
                <MessageSquare size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                {t("homepage.contact.fields.message", "Your Message About Al Saqr PaaS")}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("homepage.contact.placeholders.message", "Tell us about your IoT PaaS needs (fleet, edge, gateways)...")}
                  rows={5}
                  {...field}
                  className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {form.formState.errors.root?.serverError && (
          <FormMessage className="text-destructive text-sm font-medium p-2 bg-destructive/10 rounded-md">
            {form.formState.errors.root.serverError.message}
          </FormMessage>
        )}

        <Button 
          type="submit" 
          className="w-full group flex items-center justify-center gap-2" 
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full"
            />
          ) : (
            <Send size={18} className={cn("transition-transform", isRTL ? "group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5")} />
          )}
          {isSubmitting 
            ? t('homepage.contact.submitting', 'Sending...') 
            : t("homepage.contact.submitButton", "Send Message")}
        </Button>
      </form>
    </Form>
  );
}
